import React, { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Upload, X, Loader2, Link, ExternalLink } from 'lucide-react';
import axios from 'axios';
import api from '../lib/axios';
import { toast } from 'sonner';
import { processImageUrl } from '../utils/imageConverter';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Componente de upload de imagem com preview
 * Suporta upload local e URLs externas
 */
const ImageUpload = ({ 
  value, 
  onChange, 
  label = "Foto",
  placeholder = "Fazer upload ou colar URL...",
  className = "",
  modulo = null,
  entidadeId = null,
  uniqueId = null
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlValue, setUrlValue] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);
  const inputId = useRef(uniqueId || `file-upload-${label}-${Math.random().toString(36).slice(2, 9)}`).current;

  const getAuthHeader = () => {
    const token = localStorage.getItem('nautilus_token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  // Processa URL da imagem (converte Google Drive, URLs locais, etc)
  const getFullImageUrl = (url) => {
    if (!url) return '';
    return processImageUrl(url);
  };

  // Processar arquivo de upload
  const processFile = async (file) => {
    if (!file) return;

    // Validar tipo (mesmo limite do backend: utils/s3_client.py ALLOWED_IMAGE_TYPES)
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif'];
    const isHeicByExt = /\.(heic|heif)$/i.test(file.name || '');
    if (!validTypes.includes(file.type) && !isHeicByExt) {
      toast.error('Tipo de arquivo não suportado. Use JPG, PNG, GIF, WebP ou HEIC.');
      return;
    }

    // Validar tamanho — mesmo limite do backend: utils/s3_client.py MAX_IMAGE_SIZE (50MB)
    if (file.size > 50 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Máximo: 50MB');
      return;
    }

    setIsUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);

      // Construir URL com parâmetros de organização S3
      let uploadUrl = `${API_URL}/api/upload/image`;
      const params = [];
      if (modulo) params.push(`modulo=${encodeURIComponent(modulo)}`);
      if (entidadeId) params.push(`entidade_id=${encodeURIComponent(entidadeId)}`);
      if (params.length) uploadUrl += `?${params.join('&')}`;

      const response = await axios.post(
        uploadUrl,
        formData,
        {
          headers: {
            ...getAuthHeader().headers,
          }
        }
      );

      if (response.data.success) {
        onChange(response.data.url);
        toast.success(`Imagem enviada! Compressão: ${response.data.compression_ratio}`);
      }
    } catch (error) {
      console.error('Erro no upload:', error);
      const msg = error.response?.data?.detail || 'Erro ao enviar imagem';
      toast.error(msg);
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    await processFile(file);
  };

  // Handlers de Drag and Drop
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (dropZoneRef.current && !dropZoneRef.current.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      await processFile(file);
    }
  };

  const handleUrlSubmit = () => {
    if (urlValue.trim()) {
      onChange(urlValue.trim());
      setUrlValue('');
      setShowUrlInput(false);
      toast.success('URL da imagem adicionada');
    }
  };

  const handleRemove = () => {
    onChange('');
    toast.info('Imagem removida');
  };

  const imageUrl = getFullImageUrl(value);

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <Label className="text-xs text-gray-500">{label}</Label>
      )}
      
      {/* Preview da imagem */}
      {value && (
        <div className="relative w-full">
          <div className="relative rounded-lg overflow-hidden border bg-gray-100 aspect-video max-h-48">
            <img 
              src={imageUrl}
              alt="Preview"
              className="w-full h-full object-contain"
              onError={(e) => {
                e.target.src = '';
                e.target.alt = 'Erro ao carregar';
                e.target.className = 'w-full h-full flex items-center justify-center bg-red-50 text-red-400 text-sm';
              }}
            />
          </div>
          <Button
            type="button"
            variant="destructive"
            size="icon"
            className="absolute top-2 right-2 h-6 w-6"
            onClick={handleRemove}
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      )}
      
      {/* Área de upload com Drag and Drop */}
      {!value && !showUrlInput && (
        <div 
          ref={dropZoneRef}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          className={`relative border-2 border-dashed rounded-lg transition-all
            ${isDragging 
              ? 'border-blue-500 bg-blue-50' 
              : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
            }
            ${isUploading ? 'opacity-50 pointer-events-none' : ''}
          `}
        >
          <label htmlFor={inputId} className="block cursor-pointer p-4">
            <input
              ref={fileInputRef}
              id={inputId}
              type="file"
              accept="image/*,.heic,.heif"
              onChange={handleFileUpload}
              className="sr-only"
            />
            
            <div className="flex flex-col items-center gap-2 text-center">
              {isUploading ? (
                <>
                  <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
                  <p className="text-sm text-gray-600">Enviando...</p>
                </>
              ) : isDragging ? (
                <>
                  <Upload className="h-8 w-8 text-blue-500 animate-bounce" />
                  <p className="text-sm font-medium text-blue-600">Solte a imagem aqui!</p>
                </>
              ) : (
                <>
                  <Upload className="h-8 w-8 text-gray-400" />
                  <p className="text-sm text-gray-600">Toque para selecionar ou arraste uma imagem</p>
                  <p className="text-xs text-gray-400">JPG, PNG, GIF, WebP, HEIC (máx 50MB)</p>
                </>
              )}
            </div>
          </label>
        </div>
      )}
      
      {/* Botão para usar URL */}
      {!value && !showUrlInput && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="w-full text-xs text-gray-500"
          onClick={() => setShowUrlInput(true)}
        >
          <Link className="h-3 w-3 mr-1" />
          Ou cole uma URL
        </Button>
      )}
      
      {/* Input de URL */}
      {!value && showUrlInput && (
        <div className="flex gap-2">
          <Input
            value={urlValue}
            onChange={(e) => setUrlValue(e.target.value)}
            placeholder="Cole a URL da imagem..."
            className="h-9 text-sm flex-1"
            onKeyDown={(e) => e.key === 'Enter' && handleUrlSubmit()}
          />
          <Button
            type="button"
            variant="default"
            size="sm"
            onClick={handleUrlSubmit}
            disabled={!urlValue.trim()}
          >
            OK
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setShowUrlInput(false);
              setUrlValue('');
            }}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}
      
      {/* Mostrar URL atual se for externa */}
      {value && value.startsWith('http') && !value.includes('/api/images/') && (
        <div className="flex items-center gap-1 text-xs text-gray-400">
          <ExternalLink className="h-3 w-3" />
          <span className="truncate">{value}</span>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
